<template>
    <v-dialog
        v-model="isOpen"
        width="1040"
        :fullscreen="$vuetify.breakpoint.smAndDown"
        content-class="parts-order-dialog__overlay"
    >
        <v-card class="parts-order-dialog__card">
            <header class="parts-order-dialog__intro">
                <span class="parts-order-dialog__intro-mark" aria-hidden="true">
                    <v-icon>{{ mdiPackageVariantClosed }}</v-icon>
                </span>
                <div class="parts-order-dialog__intro-copy">
                    <span class="parts-order-dialog__eyebrow">ORDER HISTORY / PARTS</span>
                    <h2>Order #{{ orderBatchId || 'Details' }}</h2>
                    <p>Review the items, shipping address, and delivery tracking for this order.</p>
                </div>
                <v-btn icon aria-label="Close parts order details" @click="close">
                    <v-icon>{{ mdiClose }}</v-icon>
                </v-btn>
            </header>

            <section v-if="firstOrder" class="parts-order-dialog__summary" aria-label="Order summary">
                <div class="parts-order-dialog__summary-copy">
                    <span class="parts-order-dialog__eyebrow">ORDERED ITEMS</span>
                    <strong>{{ partOrder.length }} {{ partOrder.length === 1 ? 'part' : 'parts' }}</strong>
                    <span>Batch / {{ orderBatchId || 'Unavailable' }}</span>
                </div>
                <div class="parts-order-dialog__meta">
                    <span class="parts-order-dialog__status">
                        <span aria-hidden="true"></span>
                        {{ firstOrder.status || 'Order details' }}
                    </span>
                    <span v-if="firstOrder.createdAt" class="parts-order-dialog__date">
                        PLACED / {{ formatDate(firstOrder.createdAt) }}
                    </span>
                </div>
            </section>

            <div class="parts-order-dialog__scroll">
                <section class="parts-order-dialog__panel" aria-label="Parts ordered">
                    <header class="parts-order-dialog__section-heading">
                        <div>
                            <span class="parts-order-dialog__eyebrow">ORDER CONTENTS</span>
                            <h3>Parts in this order</h3>
                        </div>
                        <span class="parts-order-dialog__count">{{ partOrder.length }} line items</span>
                    </header>
                    <div class="parts-order-dialog__table-wrap">
                        <table class="parts-order-dialog__table">
                            <thead>
                                <tr>
                                    <th scope="col">#</th>
                                    <th scope="col">Serial</th>
                                    <th scope="col">Model</th>
                                    <th scope="col">Part Category</th>
                                    <th scope="col">Part Spec</th>
                                    <th scope="col">Status</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr v-for="(order, index) in partOrder" :key="order.uid || index">
                                    <td>{{ index + 1 }}</td>
                                    <td>{{ order.unit && order.unit.serial || '—' }}</td>
                                    <td>{{ order.unit && order.unit.model || '—' }}</td>
                                    <td>{{ order.category || '—' }}</td>
                                    <td>{{ order.partSpec || '—' }}</td>
                                        <td>
                                            <span class="parts-order-dialog__item-status">
                                                <span aria-hidden="true"></span>{{ order.status || '—' }}
                                            </span>
                                        </td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </section>

                <section class="parts-order-dialog__panel" aria-label="Shipping information">
                    <header class="parts-order-dialog__section-heading">
                        <div>
                            <span class="parts-order-dialog__eyebrow">DELIVERY DESTINATION</span>
                            <h3>Shipping information</h3>
                        </div>
                    </header>
                    <div v-if="shippingSections.length" class="parts-order-dialog__facts-grid">
                        <article v-for="group in shippingSections" :key="group.title" class="parts-order-dialog__fact-group">
                            <h4>{{ group.title }}</h4>
                            <dl>
                                <div v-for="fact in group.facts" :key="fact.label" class="parts-order-dialog__fact">
                                    <dt>{{ fact.label }}</dt>
                                    <dd>{{ fact.value || '—' }}</dd>
                                </div>
                            </dl>
                        </article>
                    </div>
                    <div v-else class="parts-order-dialog__empty-state">
                        Shipping information is not available for this order.
                    </div>
                </section>

                <section class="parts-order-dialog__panel" aria-label="Tracking information">
                    <header class="parts-order-dialog__section-heading">
                        <div>
                            <span class="parts-order-dialog__eyebrow">SHIPMENT STATUS</span>
                            <h3>Tracking information</h3>
                        </div>
                    </header>
                    <div v-if="trackingSections.length" class="parts-order-dialog__facts-grid">
                        <article v-for="shipment in trackingSections" :key="shipment.title" class="parts-order-dialog__fact-group">
                            <h4>{{ shipment.title }}</h4>
                            <dl>
                                <div v-for="fact in shipment.facts" :key="fact.label" class="parts-order-dialog__fact">
                                    <dt>{{ fact.label }}</dt>
                                    <dd>
                                        <a
                                            v-if="fact.label === 'Tracking URL' && fact.value"
                                            :href="fact.value"
                                            target="_blank"
                                            rel="noopener noreferrer"
                                        >Open carrier tracking</a>
                                        <a
                                            v-else-if="fact.label === 'Label' && fact.value"
                                            :href="fact.value"
                                            target="_blank"
                                            rel="noopener noreferrer"
                                        >Download label</a>
                                        <template v-else>{{ fact.value || '—' }}</template>
                                    </dd>
                                </div>
                            </dl>
                        </article>
                    </div>
                    <div v-else class="parts-order-dialog__empty-state">
                        No tracking information is available for this order yet.
                    </div>
                </section>
            </div>

            <footer class="parts-order-dialog__footer">
                <span>Order #{{ orderBatchId || 'Details' }}</span>
                <v-spacer />
                <v-btn class="parts-order-dialog__action parts-order-dialog__action--primary" depressed @click="close">
                    Close Details
                </v-btn>
            </footer>
        </v-card>
    </v-dialog>
</template>

<script>
import Vue from 'vue'
import { mdiClose, mdiPackageVariantClosed } from '@mdi/js'

/** @typedef {{ label: string, value: any }} OrderFact */
/** @typedef {{ title: string, facts: OrderFact[] }} OrderFactGroup */

export default Vue.extend({
    name: 'PartsOrderDetailsDialog',
    props: {
        value: { type: Boolean, default: false },
        partOrder: { type: Array, required: true }
    },
    data() {
        return {
            mdiClose,
            mdiPackageVariantClosed
        }
    },
    computed: {
        isOpen: {
            get() {
                return this.value
            },
            set(value) {
                this.$emit('input', value)
                if (!value) this.$emit('close')
            }
        },
        firstOrder() {
            return this.partOrder[0] || null
        },
        orderBatchId() {
            const uid = this.firstOrder && this.firstOrder.uid
            return uid ? String(uid).split('-')[0] : ''
        },
        /** @returns {OrderFactGroup[]} */
        shippingSections() {
            const shipping = this.firstOrder?.customer?.shipping
            if (!shipping) return []
            const address = shipping.address || {}
            const contact = shipping.contact || {}
            return [
                {
                    title: 'Shipping contact',
                    facts: [
                        { label: 'Company', value: address.company || contact.company },
                        { label: 'Full name', value: contact.fullName || [contact.firstName, contact.lastName].filter(Boolean).join(' ') },
                        { label: 'Email', value: contact.email },
                        { label: 'Phone', value: contact.phone }
                    ]
                },
                {
                    title: 'Delivery address',
                    facts: [
                        { label: 'Address line 1', value: address.line1 },
                        { label: 'Address line 2', value: address.line2 },
                        { label: 'City', value: address.city },
                        { label: 'State / Province', value: address.state },
                        { label: 'Country', value: address.country },
                        { label: 'Postal code', value: address.postalCode }
                    ]
                }
            ]
        },
        /** @returns {OrderFactGroup[]} */
        trackingSections() {
            const tracking = this.firstOrder?.tracking || {}
            return ['outbound', 'inbound', 'box']
                .filter((direction) => tracking[direction])
                .map((direction) => {
                    const details = tracking[direction]
                    const title = direction === 'box' ? 'Packaging shipment' : `${direction[0].toUpperCase()}${direction.slice(1)} shipment`
                    return {
                        title,
                        facts: [
                            { label: 'Carrier', value: details.carrier },
                            { label: 'Tracking number', value: details.trackingNumber },
                            { label: 'Tracking URL', value: details.trackingUrl },
                            { label: 'Label', value: details.labelUrl },
                            { label: 'Shipped', value: this.formatDate(details.sent || details.created) },
                            { label: 'Estimated arrival', value: this.formatDate(details.eta) },
                            { label: 'Received', value: this.formatDate(details.received) },
                            { label: 'Reference', value: details.reference }
                        ].filter((fact) => fact.value)
                    }
                })
        }
    },
    methods: {
        close() {
            this.isOpen = false
        },
        formatDate(value) {
            if (!value) return ''
            const date = new Date(value)
            return Number.isNaN(date.getTime()) ? '' : date.toLocaleDateString()
        }
    }
})
</script>

<style>
.parts-order-dialog__overlay {
    display: flex;
    align-items: center;
    justify-content: center;
    overflow: hidden !important;
}
.parts-order-dialog__overlay.v-dialog {
    height: calc(100vh - 32px);
    max-height: calc(100vh - 32px) !important;
    margin: 16px;
}
.parts-order-dialog__card {
    display: flex;
    width: 100%;
    height: 100%;
    max-height: 100%;
    flex-direction: column;
    overflow: hidden;
    color: #f1f2f5 !important;
    background: #282a32 !important;
    border: 1px solid rgba(255, 255, 255, 0.12) !important;
    border-radius: 10px !important;
    box-shadow: 0 22px 65px rgba(0, 0, 0, 0.42) !important;
}
.parts-order-dialog__intro {
    display: flex;
    flex: 0 0 auto;
    align-items: center;
    gap: 15px;
    padding: 21px 25px 18px;
    background: linear-gradient(115deg, rgba(219, 81, 32, 0.16), rgba(219, 81, 32, 0.035) 58%, transparent);
    border-bottom: 1px solid rgba(255, 255, 255, 0.12);
}
.parts-order-dialog__intro-mark {
    display: grid;
    flex: 0 0 42px;
    width: 42px;
    height: 42px;
    color: #fff;
    background: #db5120;
    border-radius: 10px;
    place-items: center;
}
.parts-order-dialog__intro-mark .v-icon {
    color: inherit;
    font-size: 22px;
}
.parts-order-dialog__intro-copy {
    min-width: 0;
    flex: 1;
}
.parts-order-dialog__eyebrow {
    display: block;
    margin-bottom: 4px;
    color: #f2a58b;
    font-size: 10px;
    font-weight: 600;
    letter-spacing: 1.2px;
}
.parts-order-dialog__intro h2 {
    margin: 0;
    color: #fff;
    font-family: 'Work Sans', 'Roboto', sans-serif;
    font-size: 23px;
    font-weight: 600;
    letter-spacing: -0.45px;
}
.parts-order-dialog__intro p {
    margin: 4px 0 0;
    color: #c6c8d0;
    font-size: 12px;
    line-height: 1.5;
}
.parts-order-dialog__intro > .v-btn {
    color: #c6c8d0;
}
.parts-order-dialog__summary {
    display: flex;
    flex: 0 0 auto;
    align-items: center;
    gap: 18px;
    margin: 17px 24px 0;
    padding: 14px 17px;
    background: linear-gradient(105deg, rgba(219, 81, 32, 0.17), rgba(219, 81, 32, 0.045) 65%, transparent);
    border-left: 4px solid #f0784c;
}
.parts-order-dialog__summary-copy {
    display: flex;
    min-width: 0;
    flex-direction: column;
    gap: 3px;
}
.parts-order-dialog__summary-copy .parts-order-dialog__eyebrow {
    margin-bottom: 0;
}
.parts-order-dialog__summary-copy strong {
    color: #fff;
    font-size: 16px;
    font-weight: 600;
}
.parts-order-dialog__summary-copy > span:last-child {
    color: #d4d5dc;
    font-size: 11px;
    letter-spacing: 0.45px;
    overflow-wrap: anywhere;
}
.parts-order-dialog__status,
.parts-order-dialog__date {
    display: inline-flex;
    flex: 0 0 auto;
    align-items: center;
    gap: 7px;
    padding: 7px 10px;
    color: #ffd5c4;
    background: rgba(219, 81, 32, 0.12);
    border: 1px solid rgba(240, 120, 76, 0.3);
    border-radius: 5px;
    font-size: 11px;
    font-weight: 600;
}
.parts-order-dialog__meta {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 8px;
    margin-left: auto;
}
.parts-order-dialog__status > span {
    width: 6px;
    height: 6px;
    background: #f0784c;
    border-radius: 50%;
}
.parts-order-dialog__date {
    color: #d4d5dc;
    background: rgba(255, 255, 255, 0.035);
    border-color: rgba(255, 255, 255, 0.14);
    letter-spacing: 0.4px;
}
.parts-order-dialog__scroll {
    min-height: 0;
    flex: 1 1 auto;
    overflow-y: auto;
    background: transparent !important;
    scrollbar-color: #f0784c #20222a;
    scrollbar-width: thin;
}
.parts-order-dialog__scroll::-webkit-scrollbar {
    width: 9px;
}
.parts-order-dialog__scroll::-webkit-scrollbar-track {
    background: #20222a;
    border-radius: 9px;
}
.parts-order-dialog__scroll::-webkit-scrollbar-thumb {
    background: linear-gradient(180deg, #f0784c, #c54820);
    border: 2px solid #20222a;
    border-radius: 9px;
}
.parts-order-dialog__scroll::-webkit-scrollbar-thumb:hover {
    background: #ff9a74;
}
.parts-order-dialog__panel {
    padding: 20px 24px 24px;
}
.parts-order-dialog__panel + .parts-order-dialog__panel {
    border-top: 1px solid rgba(255, 255, 255, 0.12);
}
.parts-order-dialog__section-heading {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 12px;
    margin-bottom: 14px;
}
.parts-order-dialog__section-heading h3 {
    margin: 0;
    color: #fff;
    font-family: 'Work Sans', 'Roboto', sans-serif;
    font-size: 16px;
    font-weight: 600;
}
.parts-order-dialog__count {
    padding: 6px 9px;
    color: #d4d5dc;
    background: rgba(255, 255, 255, 0.035);
    border: 1px solid rgba(255, 255, 255, 0.12);
    border-radius: 5px;
    font-size: 10px;
}
.parts-order-dialog__table-wrap {
    overflow-x: auto;
    background: #22252d;
    border: 1px solid rgba(255, 255, 255, 0.11);
    border-radius: 8px;
    box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.025);
}
.parts-order-dialog__table {
    width: 100%;
    min-width: 720px;
    border-collapse: separate;
    border-spacing: 0;
    color: #e9edf5;
    text-align: left;
}
.parts-order-dialog__table th {
    height: 44px;
    padding: 0 15px;
    color: #f2a58b;
    background: #34363e;
    border-bottom: 2px solid rgba(240, 120, 76, 0.62);
    font-size: 10px;
    font-weight: 700;
    letter-spacing: 0.7px;
    text-transform: uppercase;
    white-space: nowrap;
}
.parts-order-dialog__table th:first-child,
.parts-order-dialog__table td:first-child {
    width: 48px;
    color: #aeb5c3;
    text-align: center;
}
.parts-order-dialog__table td {
    height: 52px;
    padding: 9px 15px;
    color: #e9edf5;
    border-bottom: 1px solid rgba(255, 255, 255, 0.065);
    font-size: 12px;
    font-weight: 500;
    line-height: 1.5;
    overflow-wrap: anywhere;
}
.parts-order-dialog__table tbody tr:nth-child(even) {
    background: rgba(255, 255, 255, 0.022);
}
.parts-order-dialog__table tbody tr:hover {
    background: rgba(219, 81, 32, 0.075);
}
.parts-order-dialog__table tbody tr:last-child td {
    border-bottom: 0;
}
.parts-order-dialog__table td:nth-child(2) {
    color: #fff;
    font-family: 'Roboto Mono', Consolas, monospace;
    font-size: 11px;
}
.parts-order-dialog__item-status {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 5px 9px;
    color: #ffd5c4;
    background: rgba(219, 81, 32, 0.1);
    border: 1px solid rgba(240, 120, 76, 0.23);
    border-radius: 5px;
    font-size: 10px;
    font-weight: 600;
    white-space: nowrap;
}
.parts-order-dialog__item-status > span {
    width: 5px;
    height: 5px;
    background: #f0784c;
    border-radius: 50%;
}
.parts-order-dialog__facts-grid {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 0 25px;
}
.parts-order-dialog__fact-group {
    min-width: 0;
    padding: 15px 0 16px;
    border-top: 1px solid rgba(255, 255, 255, 0.14);
}
.parts-order-dialog__fact-group h4 {
    margin: 0 0 10px;
    color: #fff;
    font-family: 'Work Sans', 'Roboto', sans-serif;
    font-size: 14px;
    font-weight: 600;
}
.parts-order-dialog__fact-group dl {
    margin: 0;
}
.parts-order-dialog__fact {
    display: grid;
    grid-template-columns: minmax(100px, 0.8fr) minmax(0, 1.2fr);
    gap: 12px;
    padding: 8px 0;
    border-bottom: 1px solid rgba(255, 255, 255, 0.07);
}
.parts-order-dialog__fact:last-child {
    border-bottom: 0;
}
.parts-order-dialog__fact dt,
.parts-order-dialog__fact dd {
    min-width: 0;
    margin: 0;
    font-size: 11px;
    line-height: 1.5;
    overflow-wrap: anywhere;
}
.parts-order-dialog__fact dt {
    color: #bfc1ca;
}
.parts-order-dialog__fact dd {
    color: #f1f2f5;
    font-weight: 500;
    text-align: right;
}
.parts-order-dialog__fact dd a {
    color: #ff9a74;
    font-weight: 600;
    text-decoration: none;
}
.parts-order-dialog__fact dd a:hover {
    text-decoration: underline;
}
.parts-order-dialog__empty-state {
    padding: 18px 0;
    color: #c6c8d0;
    font-size: 12px;
}
.parts-order-dialog__footer {
    display: flex;
    flex: 0 0 auto;
    align-items: center;
    gap: 12px;
    min-height: 66px;
    padding: 12px 24px max(15px, env(safe-area-inset-bottom));
    color: #aeb5c3;
    background: #282a32;
    border-top: 1px solid rgba(255, 255, 255, 0.12);
    font-size: 11px;
}
.parts-order-dialog__action.v-btn {
    min-width: 0;
    min-height: 38px;
    padding: 0 14px;
    color: #fff !important;
    background: linear-gradient(135deg, #ed7042, #cb481e) !important;
    border: 1px solid rgba(255, 177, 145, 0.38);
    border-radius: 6px;
    box-shadow: 0 5px 14px rgba(197, 72, 32, 0.2);
    font-size: 11px;
    font-weight: 600;
    letter-spacing: 0.15px;
    text-transform: none;
    transition: background-color 140ms ease, box-shadow 140ms ease, transform 140ms ease;
}
.parts-order-dialog__action.v-btn::before {
    opacity: 0 !important;
}
.parts-order-dialog__action.v-btn:hover {
    transform: translateY(-1px);
    box-shadow: 0 7px 18px rgba(197, 72, 32, 0.3);
}
.parts-order-dialog__action.v-btn:focus-visible {
    outline: 2px solid #ff9a74;
    outline-offset: 3px;
}
@media (max-width: 700px) {
    .parts-order-dialog__overlay {
        align-items: stretch;
    }
    .parts-order-dialog__overlay.v-dialog {
        width: 100% !important;
        height: 100vh;
        max-height: 100vh !important;
        margin: 0;
    }
    @supports (height: 100dvh) {
        .parts-order-dialog__overlay.v-dialog {
            height: 100dvh;
            max-height: 100dvh !important;
        }
    }
    .parts-order-dialog__card {
        height: 100vh;
        max-height: 100vh;
        border: 0 !important;
        border-radius: 0 !important;
    }
    .parts-order-dialog__intro {
        gap: 11px;
        padding: 16px 16px 14px;
    }
    .parts-order-dialog__intro-mark {
        flex-basis: 38px;
        width: 38px;
        height: 38px;
    }
    .parts-order-dialog__intro h2 {
        font-size: 20px;
    }
    .parts-order-dialog__summary {
        flex-wrap: wrap;
        gap: 10px;
        margin: 13px 15px 0;
        padding: 12px;
    }
    .parts-order-dialog__status,
    .parts-order-dialog__date {
        margin-left: 0;
    }
    .parts-order-dialog__panel {
        padding: 17px 15px 20px;
    }
    .parts-order-dialog__facts-grid {
        grid-template-columns: minmax(0, 1fr);
    }
    .parts-order-dialog__footer {
        padding-right: 15px;
        padding-left: 15px;
    }
}
</style>
